import { useState, useEffect } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import axios from "axios";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Client token management
const getClientToken = () => {
  let token = localStorage.getItem('client_vote_token');
  if (!token) {
    // Generate a unique client token
    token = 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('client_vote_token', token);
  }
  return token;
};

const VoteWidget = ({ codeId, className = "" }) => {
  const [voteData, setVoteData] = useState({
    like_count: 0,
    dislike_count: 0,
    user_vote: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load initial vote data
  useEffect(() => {
    if (codeId) {
      fetchVoteData();
    }
  }, [codeId]);

  const fetchVoteData = async () => {
    try {
      const clientToken = getClientToken();
      const response = await axios.get(`${API}/codes/${codeId}/votes`, {
        params: { client_token: clientToken }
      });
      setVoteData(response.data);
    } catch (error) {
      console.error("Error fetching vote data:", error);
      // Don't show error to user for initial load
    }
  };

  const handleVote = async (voteType) => {
    if (loading) return;

    // Prevent voting if user already voted
    if (voteData.user_vote) {
      setError(`You already voted '${voteData.user_vote}' on this code`);
      setTimeout(() => setError(""), 3000);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const clientToken = getClientToken();
      const response = await axios.post(`${API}/codes/${codeId}/vote`, {
        vote_type: voteType,
        client_token: clientToken
      });

      if (response.data.success) {
        // Update local state with new counts
        setVoteData({
          like_count: response.data.like_count,
          dislike_count: response.data.dislike_count,
          user_vote: response.data.user_vote
        });
      } else {
        // User already voted
        setError(response.data.message);
        setVoteData({
          like_count: response.data.like_count,
          dislike_count: response.data.dislike_count,
          user_vote: response.data.user_vote
        });
        setTimeout(() => setError(""), 3000);
      }
    } catch (error) {
      console.error("Error submitting vote:", error);
      setError("Failed to submit vote. Please try again.");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const getTotalVotes = () => {
    return voteData.like_count + voteData.dislike_count;
  };

  const getLikePercentage = () => {
    const total = getTotalVotes();
    if (total === 0) return 50;
    return Math.round((voteData.like_count / total) * 100);
  };

  return (
    <div className={`vote-widget ${className}`}>
      {/* Vote Buttons */}
      <div className="flex items-center gap-2">
        {/* Like Button - Green for Good Reviews */}
        <button
          onClick={() => handleVote('like')}
          disabled={loading || voteData.user_vote === 'like'}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            voteData.user_vote === 'like'
              ? 'bg-green-500 text-white border border-green-600 shadow-md'
              : voteData.user_vote === 'dislike'
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
              : 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100 hover:text-green-800 hover:border-green-400'
          } ${loading ? 'opacity-50 cursor-wait' : ''}`}
          title={voteData.user_vote ? `You voted '${voteData.user_vote}'` : 'Like this code (Good Review)'}
        >
          <ThumbsUp className={`w-4 h-4 ${voteData.user_vote === 'like' ? 'fill-current' : ''}`} />
          <span>{voteData.like_count}</span>
        </button>

        {/* Dislike Button - Red for Bad Reviews */}
        <button
          onClick={() => handleVote('dislike')}
          disabled={loading || voteData.user_vote === 'dislike'}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
            voteData.user_vote === 'dislike'
              ? 'bg-red-500 text-white border border-red-600 shadow-md'
              : voteData.user_vote === 'like'
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
              : 'bg-red-50 text-red-700 border border-red-300 hover:bg-red-100 hover:text-red-800 hover:border-red-400'
          } ${loading ? 'opacity-50 cursor-wait' : ''}`}
          title={voteData.user_vote ? `You voted '${voteData.user_vote}'` : 'Dislike this code (Bad Review)'}
        >
          <ThumbsDown className={`w-4 h-4 ${voteData.user_vote === 'dislike' ? 'fill-current' : ''}`} />
          <span>{voteData.dislike_count}</span>
        </button>

        {/* Vote Ratio Bar (if there are votes) */}
        {getTotalVotes() > 0 && (
          <div className="flex items-center gap-2 ml-2">
            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${getLikePercentage()}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">
              {getLikePercentage()}%
            </span>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
          {error}
        </div>
      )}

      {/* Vote Status */}
      {voteData.user_vote && !error && (
        <div className="mt-1 text-xs text-gray-500">
          You voted: <span className="font-medium capitalize">{voteData.user_vote}</span>
        </div>
      )}
    </div>
  );
};

export default VoteWidget;